import { DSLClasses } from '@cadet-dsl';
import { expectDefined, immerable, immerClone } from '@utils';
import type { ElementSpec } from './ElementSpec';

interface DocClass {
  isBuiltIn: boolean;
  parent: string | undefined;
  classAttrs: Record<string, unknown>;
}

/**
 * Manages class definitions and attribute resolution at runtime.
 */
export class DocClasses {
  [immerable] = true;

  private classInfo = new Map<string, DocClass>();

  constructor(dslClasses: DSLClasses) {
    this.load(dslClasses);
  }

  /** Populates runtime class structures by deep copying from source. */
  private load(dslClasses: DSLClasses): void {
    const tree = dslClasses.tree;
    this.classInfo.clear();
    for (const className of tree.nodes) {
      let parent: string | undefined = undefined;
      try {
        parent = tree.parent(className);
      } catch {
        /* root */
      }
      const nodeData = tree.nodeData(className);
      const isBuiltIn = nodeData.isBuiltIn;
      const classAttrs = immerClone(nodeData.attrs);
      this.classInfo.set(className, {
        isBuiltIn,
        parent,
        classAttrs
      });
    }
  }

  // --- Utilities for Deep Merge ---
  private isPlainObject(value: unknown): value is Record<string, unknown> {
    return (
      value !== undefined &&
      value !== null &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      value.constructor.name === 'Object'
    );
  }

  /**
   * Deeply merges parent and child attributes.
   * - If both are objects, merges recursively.
   * - If the child has a key not present in the parent, it is added.
   * - If the parent has a key not present in the child, it is kept.
   * - The returned object is immutable.
   */
  private mergeParentChild(
    parent: Record<string, unknown>,
    child: Record<string, unknown>
  ): Record<string, unknown> {
    const result: Record<string, unknown> = {};
    for (const key in parent) {
      if (Object.prototype.hasOwnProperty.call(parent, key)) {
        const parentValue = parent[key];
        if (this.isPlainObject(parentValue)) {
          result[key] = { ...parentValue };
        } else {
          result[key] = parentValue;
        }
      }
    }
    for (const key in child) {
      if (Object.prototype.hasOwnProperty.call(child, key)) {
        const childValue = child[key];
        const parentValue = result[key];
        if (this.isPlainObject(childValue) && this.isPlainObject(parentValue)) {
          result[key] = this.mergeParentChild(parentValue, childValue);
        } else {
          result[key] = childValue;
        }
      }
    }
    return Object.freeze(result);
  }
  // --- End Deep Merge Utilities ---

  /** Gets the inheritance path (root first). */
  private getClassAncestry(className: string): string[] {
    const path: string[] = [];
    let current: string | undefined = className;
    while (current && this.classInfo.has(current)) {
      path.push(current);
      const info = this.classInfo.get(current);
      current = info ? info.parent : undefined;
    }
    return path.reverse();
  }

  // --- Public Methods ---

  /** Resolves the final value for an attribute (Instance/State > Class > Superclass). */
  public getInstanceAttr(elementData: ElementSpec, key: string): unknown {
    let value = elementData.attrs[key];
    let currentClassName: string | undefined = elementData.cls;
    while (currentClassName !== undefined) {
      const currentClassInfo: DocClass = expectDefined(
        this.classInfo.get(currentClassName)
      );
      const currentDefault = currentClassInfo.classAttrs[key];
      if (this.isPlainObject(currentDefault)) {
        if (value === undefined) {
          value = {};
        }
        if (this.isPlainObject(value)) {
          value = this.mergeParentChild(currentDefault, value);
        }
      } else {
        if (value === undefined) {
          value = currentDefault;
        }
        if (value !== undefined && !this.isPlainObject(value)) {
          // Value now has a definitive non-object form
          break;
        }
      }
      currentClassName = currentClassInfo.parent;
    }

    return value;
  }

  /** Checks if a class is defined in the runtime. */
  public hasClass(className: string): boolean {
    return this.classInfo.has(className);
  }

  public isBuiltInClass(className: string): boolean {
    const info = this.checkHasClass(className);
    return info.isBuiltIn;
  }

  public isAbstractClass(className: string): boolean {
    const info = this.checkHasClass(className);
    return !!info.classAttrs.isAbstract;
  }

  public checkNotBuiltInClass(className: string): void {
    if (this.isBuiltInClass(className)) {
      throw new Error(`Cannot modify built-in class '${className}'.`);
    }
  }

  /** Ensures the specified class exists, or throws an error. */
  public checkHasClass(className: string): DocClass {
    const info = this.classInfo.get(className);
    if (!info) throw new Error(`Unknown class '${className}'.`);
    return info;
  }

  /** Checks class inheritance. */
  public isClassKindOf(className: string, potentialSuperClass: string): boolean {
    this.checkHasClass(className);
    this.checkHasClass(potentialSuperClass);
    if (className === potentialSuperClass) return true;
    let current: string | undefined = className;
    while (current) {
      const info = this.classInfo.get(current);
      const parent = info ? info.parent : undefined;
      if (parent === potentialSuperClass) return true;
      current = parent;
    }
    return false;
  }

  public isUnitClass(className: string): boolean {
    return className === 'Unit' || this.isClassKindOf(className, 'Unit');
  }

  public isGroupClass(className: string): boolean {
    return className === 'Group' || this.isClassKindOf(className, 'Group');
  }

  /** Gets all *directly defined* attributes for a class. */
  public getClassAttrs(className: string): Record<string, unknown> {
    this.checkHasClass(className);
    return this.classInfo.get(className)?.classAttrs ?? {};
  }

  /** Gets all attributes for a class, *flattened* through inheritance. */
  public getFlattenedClassAttrs(className: string): Record<string, unknown> {
    this.checkHasClass(className);
    const ancestry = this.getClassAncestry(className);
    let merged: Record<string, unknown> = {};
    for (const ancestorClassName of ancestry) {
      const info = this.classInfo.get(ancestorClassName);
      if (info) {
        merged = this.mergeParentChild(merged, info.classAttrs);
      }
    }
    return merged;
  }

  /**
   * Returns the details needed to reconstruct class definitions.
   * @returns A Map where keys are class names and values contain parent and direct attributes.
   */
  public getAllClassDetails(): Map<
    string,
    { parent: string | undefined; attrs: Record<string, unknown> }
  > {
    const details = new Map<
      string,
      { parent: string | undefined; attrs: Record<string, unknown> }
    >();
    for (const [className, runtimeInfo] of this.classInfo.entries()) {
      // Important: Use immerClone to prevent internal state leakage
      details.set(className, {
        parent: runtimeInfo.parent,
        attrs: immerClone(runtimeInfo.classAttrs)
      });
    }
    return details;
  }

  /** Adds a brand-new class definition. */
  public addClass(
    className: string,
    parent: string | undefined = undefined,
    attrs: Record<string, unknown> = {}
  ): void {
    if (this.classInfo.has(className)) {
      throw new Error(`Class '${className}' already exists.`);
    }
    if (parent && !this.classInfo.has(parent)) {
      throw new Error(`Parent class '${parent}' does not exist.`);
    }
    this.classInfo.set(className, {
      isBuiltIn: false,
      parent,
      classAttrs: immerClone(attrs)
    });
  }

  /** Replaces the *entire* default-attribute object for a class. */
  public setClassAttrs(className: string, attrs: Record<string, unknown>): void {
    this.checkNotBuiltInClass(className);
    const info = this.classInfo.get(className);
    if (!info) throw new Error(`Unknown class '${className}'.`);
    info.classAttrs = immerClone(attrs);
  }

  /** Deep-merges the supplied attrs into the existing defaults. */
  public updateClassAttrs(className: string, attrs: Record<string, unknown>): void {
    this.checkNotBuiltInClass(className);
    const info = this.classInfo.get(className);
    if (!info) throw new Error(`Unknown class '${className}'.`);
    info.classAttrs = this.mergeParentChild(info.classAttrs, attrs);
  }

  /**
   * Applies include-provided default attributes into a class, even if it is built-in.
   * Use only when merging defaults from declared includes.
   */
  public mergeIncludeClassAttrs(className: string, attrs: Record<string, unknown>): void {
    const info = this.classInfo.get(className);
    if (!info) throw new Error(`Unknown class '${className}'.`);
    info.classAttrs = this.mergeParentChild(info.classAttrs, attrs);
  }

  /** Removes a class (fails if other classes depend on it). */
  public removeClass(className: string): void {
    this.checkNotBuiltInClass(className);
    for (const [, info] of this.classInfo) {
      if (info.parent === className) {
        throw new Error(
          `Cannot remove '${className}'; it is a superclass of another class.`
        );
      }
    }
    if (!this.classInfo.delete(className)) {
      throw new Error(`Unknown class '${className}'.`);
    }
  }

  // --- Runtime Modification Placeholders ---
}
